import { useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Auth from "./pages/Auth";
import SharedSpot from "./pages/SharedSpot";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import RankingsUpdater from "./pages/RankingsUpdater";
import { AuthProvider } from "./hooks/use-auth";
import { SubscriptionProvider } from "./hooks/use-subscription-provider";
import InstallPrompt from "@/components/InstallPrompt";
import SupportButton from "@/components/SupportButton";
import MobileGate from "@/components/MobileGate";
import SubscribePage from '@/pages/SubscribePage';

const queryClient = new QueryClient();

function useServiceWorker() {
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    const register = async () => {
      try {
        const registration = await navigator.serviceWorker.register("/sw.js", {
          scope: "/",
        });

        registration.addEventListener("updatefound", () => {
          const newWorker = registration.installing;
          if (!newWorker) return;
          newWorker.addEventListener("statechange", () => {
            if (
              newWorker.state === "installed" &&
              navigator.serviceWorker.controller
            ) {
              // Wire this to your Sonner toast if you want a "update available" banner
              console.info("[Barrelcast] New version available — reload to update.");
            }
          });
        });

        console.info("[Barrelcast] SW registered:", registration.scope);
      } catch (err) {
        console.error("[Barrelcast] SW registration failed:", err);
      }
    };

    if (document.readyState === "complete") {
      register();
    } else {
      window.addEventListener("load", register, { once: true });
    }
  }, []);
}

const App = () => {
  useServiceWorker();

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <MobileGate>
          <BrowserRouter>
            <AuthProvider>
              <SubscriptionProvider>
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/spot/:token" element={<SharedSpot />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/rankingsupdater" element={<RankingsUpdater />} />
                  <Route path="/subscribe" element={<SubscribePage />} />
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </SubscriptionProvider>
            </AuthProvider>
          </BrowserRouter>
        </MobileGate>
      </TooltipProvider>
      <InstallPrompt />
      <SupportButton />
    </QueryClientProvider>
  );
};

export default App;
